import { COMMON, REPORT } from "../config/apiEndPoint";
import { GET, POST, POST_V2 } from "config/api.config";
import {
  resolveAsyncClientFilterPageName,
  resolveSystemUsersFilterPageName,
} from "helpers/metadataFilterHelpers";
import {
  getCandidateDetailsForUsingCallDetails,
  submitCandidateFilterData,
  submitFadvCsvCandidateAutocomplete,
} from "model/CandidateModel";
import { getDefaultUserList } from "model/UserModel";

/** Match onboarding company field: fetch when trimmed length is greater than this (i.e. 4+ chars). */
export const ASYNC_FILTER_INPUT_MIN_LENGTH = 3;

/** Collapse nbsp / repeated whitespace — avoids async-select search mismatches on multi-word labels. */
export const normalizeAsyncFilterSearchText = (text = "") =>
  String(text ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** First token for APIs that only support single-word `nameContains` (e.g. `common/source`). */
export const resolveAsyncFilterApiSearchText = (searchText = "") => {
  const normalized = normalizeAsyncFilterSearchText(searchText);
  if (!normalized) return "";
  return normalized.split(" ")[0] || normalized;
};

/** Client-side match for multi-word queries after a broader API search. */
export const filterAsyncSelectOptionsBySearchText = (options = [], searchText = "") => {
  const normalizedSearch = normalizeAsyncFilterSearchText(searchText).toLowerCase();
  if (!normalizedSearch) return Array.isArray(options) ? options : [];
  const words = normalizedSearch.split(" ").filter(Boolean);
  return (Array.isArray(options) ? options : []).filter((option) => {
    const label = normalizeAsyncFilterSearchText(option?.label).toLowerCase();
    if (!label) return false;
    if (label.includes(normalizedSearch)) return true;
    return words.every((word) => label.includes(word));
  });
};

export const ASYNC_FILTER_PAGE_SIZE = 200;

/** @deprecated Prefer resolveAsyncClientFilterPageName — V2 client body is `{ pageName, nameContains }` only. */
export const ASYNC_CLIENT_FILTER_POST_BODY_DEFAULT = {
  checkAccess: true,
  checkActive: -1,
  useCallBagRules: true,
  useOnboardingBagRules: false
};

/** @deprecated V2 client dropdown no longer uses bag-rule bodies. */
export const ASYNC_CLIENT_FILTER_POST_BODY_CALL_BAG = {
  checkAccess: true,
  useCallBagRules: true,
  checkActive: -1,
};

/** @deprecated V2 client dropdown no longer uses bag-rule bodies. */
export const resolveAsyncClientFilterPostBody = (pageName = "") => {
  const resolvedPageName = resolveAsyncClientFilterPageName(pageName);
  return resolvedPageName
    ? { pageName: resolvedPageName, nameContains: null }
    : { ...ASYNC_CLIENT_FILTER_POST_BODY_DEFAULT };
};

const recordsFromFilterResponse = (response) => {
  const d = response?.data ?? {};
  if (Array.isArray(d?.records)) return d.records;
  if (Array.isArray(d)) return d;
  return [];
};

export const mapClientItemToOption = (item) => {
  const value = String(item?.id ?? item?._id ?? item?.clientId ?? "").trim();
  if (!value) return null;
  const label =
    item?.display ||
    item?.client ||
    item?.clientName ||
    item?.name ||
    item?.title ||
    item?.company ||
    "Client";
  return { value, label };
};

export const mapSourceItemToOption = (item) => {
  const value = String(item?.id ?? item?._id ?? "").trim();
  if (!value) return null;
  const label =
    normalizeAsyncFilterSearchText(
      item?.display ||
        item?.name ||
        item?.sourceName ||
        item?.title ||
        item?.label ||
        "Source"
    ) || "Source";
  return { value, label };
};

export const mapUserItemToOption = (item) => {
  const value = String(item?.id ?? item?._id ?? item?.userId ?? "").trim();
  if (!value) return null;
  const fullName = `${item?.firstName || ""} ${item?.lastName || ""}`.trim();
  const label =
    fullName ||
    item?.display ||
    item?.userName ||
    item?.name ||
    item?.userEmail ||
    item?.email ||
    "User";
  return { value, label };
};

const buildSystemUsersFilterRequest = (pageName, overrides = {}) => {
  const { nameContains, search, userId, ...rest } = overrides;
  const body = {
    pageName: resolveSystemUsersFilterPageName(pageName),
    ...rest,
  };
  const resolvedSearch = search ?? nameContains;
  if (userId != null && String(userId).trim()) {
    body.userId = String(userId).trim();
  }
  if (resolvedSearch != null && String(resolvedSearch).trim()) {
    body.search = String(resolvedSearch).trim();
  }
  return body;
};

const fetchSystemUsersForFilter = async (pageName, overrides = {}) => {
  const resolvedPageName = resolveSystemUsersFilterPageName(pageName);
  if (!resolvedPageName) return null;
  return POST(COMMON.SYSTEM_USERS, buildSystemUsersFilterRequest(resolvedPageName, overrides));
};

export const mapCandidateItemToOption = (item) => {
  const value = String(item?.id ?? item?._id ?? item?.candidateId ?? "").trim();
  if (!value) return null;
  const fullName = `${item?.firstName || ""} ${item?.lastName || ""}`.trim();
  const label =
    fullName ||
    item?.candidateName ||
    item?.name ||
    item?.displayName ||
    "Candidate";
  return { value, label };
};

/** `candidate/csv/candidate-autocomplete` — `{ candidateId, candidateClientId, display }`. */
export const mapFadvCsvCandidateItemToOption = (item) => {
  const value = String(item?.candidateId ?? item?.id ?? item?._id ?? "").trim();
  if (!value) return null;
  const label =
    item?.display ||
    item?.candidateName ||
    `${item?.firstName || ""} ${item?.lastName || ""}`.trim() ||
    item?.name ||
    item?.displayName ||
    "Candidate";
  return { value, label };
};

export const mapFadvCsvCandidateClientItemToOption = (item) => {
  const value = String(
    item?.candidateClientId ?? item?.candidateId ?? item?.id ?? item?._id ?? ""
  ).trim();
  if (!value) return null;
  const label =
    item?.display ||
    item?.candidateName ||
    `${item?.firstName || ""} ${item?.lastName || ""}`.trim() ||
    item?.name ||
    item?.displayName ||
    "Candidate";
  return { value, label };
};

const fadvCsvCandidatesFromAutocompleteResponse = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.records)) return data.records;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const mapClientFilterResponseToOptions = (response) => {
  const list = recordsFromFilterResponse(response);
  const map = new Map();
  (Array.isArray(list) ? list : []).forEach((item) => {
    const opt = mapClientItemToOption(item);
    if (opt) map.set(opt.value, opt);
  });
  return Array.from(map.values());
};

/** POST V2 `common/client` — `{ pageName, nameContains }`. */
const buildV2ClientFilterRequest = (pageName = "", nameContains = null) => {
  const resolvedPageName = resolveAsyncClientFilterPageName(pageName);
  const trimmed =
    nameContains == null ? null : String(nameContains).trim() || null;
  return {
    pageName: resolvedPageName,
    nameContains: trimmed,
  };
};

const postV2CommonClient = async (pageName = "", nameContains = null) => {
  const body = buildV2ClientFilterRequest(pageName, nameContains);
  if (!body.pageName) return null;
  return POST_V2(COMMON.CLIENT, body);
};

const buildInProgressClientsGetUrl = ({ nameContains = "", _Id = "" } = {}) => {
  const params = new URLSearchParams();
  const search = String(nameContains ?? "").trim();
  const id = String(_Id ?? "").trim();
  if (search) params.set("nameContains", search);
  if (id) params.set("_Id", id);
  const query = params.toString();
  return query ? `${COMMON.IN_PROGRESS_CLIENTS}?${query}` : COMMON.IN_PROGRESS_CLIENTS;
};

const buildClientReportClientsGetUrl = ({ nameContains = "", _Id = "" } = {}) => {
  const params = new URLSearchParams();
  const search = String(nameContains ?? "").trim();
  const id = String(_Id ?? "").trim();
  if (search) params.set("nameContains", search);
  if (id) params.set("_Id", id);
  const query = params.toString();
  return query
    ? `${REPORT.CLIENT_OPTIONS_CLIENTS}?${query}`
    : REPORT.CLIENT_OPTIONS_CLIENTS;
};

const recordsFromClientReportClientsResponse = (response) => {
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  const inner = data?.records ?? data?.rows ?? data?.items ?? data?.data;
  return Array.isArray(inner) ? inner : [];
};

const mapClientReportClientItemToOption = (item) => {
  if (typeof item === "string") {
    const value = item.trim();
    return value ? { value, label: value } : null;
  }
  const fromClient = mapClientItemToOption(item);
  if (fromClient) return fromClient;
  const value = String(item?.value ?? "").trim();
  const label = String(item?.label ?? value).trim();
  return value ? { value, label: label || value } : null;
};

const mapClientReportClientsResponseToOptions = (response) => {
  const list = recordsFromClientReportClientsResponse(response);
  const map = new Map();
  (Array.isArray(list) ? list : []).forEach((item) => {
    const opt = mapClientReportClientItemToOption(item);
    if (opt) map.set(opt.value, opt);
  });
  return Array.from(map.values());
};

/** GET `common/in-progress/clients` — in-progress report client select (not V2 `common/client`). */
export async function fetchInProgressClientOptionsForSelect(searchText = "") {
  const response = await GET(
    buildInProgressClientsGetUrl({ nameContains: searchText || "" })
  );
  return mapClientFilterResponseToOptions(response);
}

export async function fetchInProgressClientOptionById(id) {
  if (!id) return null;
  const idStr = String(id).trim();
  const response = await GET(buildInProgressClientsGetUrl({ _Id: idStr }));
  const list = recordsFromFilterResponse(response);
  const row =
    list.find(
      (item) =>
        String(item?.id ?? item?._id ?? item?.clientId ?? "").trim() === idStr
    ) ?? list[0];
  if (!row || typeof row !== "object") return null;
  return mapClientItemToOption(row);
}

/** GET `Report/client/options/clients` — client report client select (not V2 `common/client`). */
export async function fetchClientReportClientOptionsForSelect(searchText = "") {
  const response = await GET(
    buildClientReportClientsGetUrl({ nameContains: searchText || "" })
  );
  return mapClientReportClientsResponseToOptions(response);
}

export async function fetchClientReportClientOptionById(id) {
  if (!id) return null;
  const idStr = String(id).trim();
  const response = await GET(buildClientReportClientsGetUrl({ _Id: idStr }));
  const list = recordsFromClientReportClientsResponse(response);
  const row =
    list.find((item) => {
      const itemValue = String(
        item?.id ?? item?._id ?? item?.clientId ?? item?.value ?? ""
      ).trim();
      return itemValue === idStr;
    }) ?? list[0];
  if (!row || typeof row !== "object") return null;
  return mapClientReportClientItemToOption(row);
}

/** V2 `common/client` list — `{ data: [{ id, display }, …] }`. */
const recordsFromV2ClientResponse = (response) => {
  if (Array.isArray(response)) return response;
  const d = response?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.records)) return d.records;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(response?.records)) return response.records;
  return [];
};

const mapV2ClientResponseToOptions = (response) => {
  const list = recordsFromV2ClientResponse(response);
  const map = new Map();
  (Array.isArray(list) ? list : []).forEach((item) => {
    const opt = mapClientItemToOption(item);
    if (opt) map.set(opt.value, opt);
  });
  return Array.from(map.values());
};

/** POST V2 `common/client` — `{ pageName, nameContains }` via `REACT_APP_API_URL_V2` (`AsyncClientSelect` only). */
export async function fetchClientOptionsForSelect(searchText = "", context = {}) {
  const trimmedSearch =
    searchText == null ? "" : String(searchText).trim();
  const pageName =
    context?.restartClientBag && typeof context.restartClientBag === "object"
      ? "OnboardingAdd"
      : context?.pageName;
  const response = await postV2CommonClient(pageName, trimmedSearch || null);
  return mapV2ClientResponseToOptions(response);
}

export async function fetchClientOptionById(id, context = {}) {
  if (!id) return null;
  const idStr = String(id).trim();
  const pageName =
    context?.restartClientBag && typeof context.restartClientBag === "object"
      ? "OnboardingAdd"
      : context?.pageName;
  const response = await postV2CommonClient(pageName, null);
  const list = recordsFromV2ClientResponse(response);
  const row =
    list.find(
      (item) =>
        String(item?.id ?? item?._id ?? item?.clientId ?? "").trim() === idStr
    ) ?? null;
  if (!row || typeof row !== "object") return null;
  return mapClientItemToOption(row);
}

const buildCommonSourceGetUrl = ({ nameContains = "", _Id = "" } = {}) => {
  const params = new URLSearchParams();
  const search = normalizeAsyncFilterSearchText(nameContains);
  const id = String(_Id ?? "").trim();
  if (search) params.set("nameContains", search);
  if (id) params.set("_Id", id);
  const query = params.toString();
  return query ? `${COMMON.SOURCE}?${query}` : COMMON.SOURCE;
};

const recordsFromCommonSourceResponse = (response) => {
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.records)) return data.records;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export async function fetchSourceOptionsForSelect(searchText = "") {
  const normalizedSearch = normalizeAsyncFilterSearchText(searchText);
  if (!normalizedSearch) return [];

  // `common/source` matches a single token — fetch the first word, filter full phrase locally.
  const apiSearch = resolveAsyncFilterApiSearchText(normalizedSearch);
  const response = await GET(buildCommonSourceGetUrl({ nameContains: apiSearch }));
  const list = recordsFromCommonSourceResponse(response);
  const map = new Map();
  (Array.isArray(list) ? list : []).forEach((item) => {
    const opt = mapSourceItemToOption(item);
    if (opt) map.set(opt.value, opt);
  });
  return filterAsyncSelectOptionsBySearchText(Array.from(map.values()), normalizedSearch);
}

export async function fetchSourceOptionById(id) {
  if (!id) return null;
  const idStr = String(id).trim();
  const response = await GET(buildCommonSourceGetUrl({ _Id: idStr }));
  const list = recordsFromCommonSourceResponse(response);
  const row =
    list.find(
      (item) => String(item?.id ?? item?._id ?? "").trim() === idStr
    ) ?? list[0];
  if (!row || typeof row !== "object") return null;
  return mapSourceItemToOption(row);
}

const userRecordsFromResponse = (response) => {
  const d = response?.data;
  if (Array.isArray(d?.records)) return d.records;
  if (Array.isArray(d)) return d;
  if (d && typeof d === "object" && !Array.isArray(d)) return [d];
  return [];
};

export async function fetchUserOptionsForSelect(searchText = "", context = {}) {
  const pageName = resolveSystemUsersFilterPageName(context?.pageName);
  if (pageName) {
    const response = await fetchSystemUsersForFilter(pageName, {
      nameContains: searchText || "",
    });
    const list = userRecordsFromResponse(response);
    const map = new Map();
    list.forEach((item) => {
      const opt = mapUserItemToOption(item);
      if (opt) map.set(opt.value, opt);
    });
    return Array.from(map.values());
  }

  const response = await getDefaultUserList({
    nameContains: searchText || "",
    name: searchText || "",
    pageNumber: 1,
    pageSize: ASYNC_FILTER_PAGE_SIZE,
  });
  const list = userRecordsFromResponse(response);
  const map = new Map();
  list.forEach((item) => {
    const opt = mapUserItemToOption(item);
    if (opt) map.set(opt.value, opt);
  });
  return Array.from(map.values());
}

export async function fetchUserOptionById(id, context = {}) {
  if (!id) return null;
  const idStr = String(id).trim();
  const pageName = resolveSystemUsersFilterPageName(context?.pageName);
  if (pageName) {
    let response = await fetchSystemUsersForFilter(pageName, { userId: idStr });
    let list = userRecordsFromResponse(response);
    if (!list.length) {
      response = await fetchSystemUsersForFilter(pageName, { nameContains: idStr });
      list = userRecordsFromResponse(response);
    }
    const row =
      list.find(
        (item) =>
          String(item?.id ?? item?._id ?? item?.userId ?? "").trim() === idStr
      ) ?? list[0];
    return row ? mapUserItemToOption(row) : null;
  }

  const response = await getDefaultUserList({ _Id: idStr });
  const list = userRecordsFromResponse(response);
  const first = list[0];
  return first ? mapUserItemToOption(first) : null;
}

export async function fetchCandidateOptionsForSelect(searchText = "", clientId = "") {
  const scopedClientId = String(clientId ?? "").trim();
  if (!scopedClientId) return [];
  const response = await submitCandidateFilterData({
    clientId: scopedClientId,
    nameContains: searchText || "",
    pageNumber: 1,
    pageSize: ASYNC_FILTER_PAGE_SIZE,
  });
  const list = recordsFromFilterResponse(response);
  const map = new Map();
  (Array.isArray(list) ? list : []).forEach((item) => {
    const opt = mapCandidateItemToOption(item);
    if (opt) map.set(opt.value, opt);
  });
  return Array.from(map.values());
}

export async function fetchFadvCsvCandidateOptionsForSelect(searchText = "", context = {}) {
  const scopedClientId = String(context?.clientId ?? "").trim();
  const pageName = String(context?.pageName ?? "").trim().replace(/[\s_-]+/g, "").toLowerCase();
  const allowWithoutClientId = pageName === "verifyapplicationfile";
  if (!scopedClientId && !allowWithoutClientId) return [];
  const response = await submitFadvCsvCandidateAutocomplete({
    search: searchText || "",
    ...(scopedClientId ? { clientId: scopedClientId } : {}),
  });
  const list = fadvCsvCandidatesFromAutocompleteResponse(response);
  const map = new Map();
  const mapOption =
    pageName === "verifyapplicationfile"
      ? mapFadvCsvCandidateClientItemToOption
      : mapFadvCsvCandidateItemToOption;
  (Array.isArray(list) ? list : []).forEach((item) => {
    const opt = mapOption(item);
    if (opt) map.set(opt.value, opt);
  });
  return Array.from(map.values());
}

export async function fetchFadvCsvCandidateOptionById(id, context = {}) {
  if (!id) return null;
  const idStr = String(id).trim();
  const options = await fetchFadvCsvCandidateOptionsForSelect(idStr, context);
  return (
    options.find((option) => String(option?.value ?? "").trim() === idStr) ||
    options[0] ||
    null
  );
}

export async function fetchCandidateOptionById(id) {
  if (!id) return null;
  const response = await getCandidateDetailsForUsingCallDetails({
    candidateId: String(id),
  });
  const row = response?.data;
  if (!row || typeof row !== "object") return null;
  return mapCandidateItemToOption(row);
}
